import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "react-day-picker/style.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  setSessionKeys,
  clearSessionKeys,
  unlockEncryptionNoPassword,
  unwrapKey
} from "@/utils/crypto";
import { fetchAuthMe } from "@/utils/authMe";
import { shouldRetryIdempotentRequest } from "@/utils/apiError";

// Eagerly loaded pages (critical path)
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import FaqPage from "@/pages/FaqPage";

// Lazy loaded pages (loaded on demand)
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const TransactionPage = lazy(() => import("@/pages/TransactionPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const TemplateEditPage = lazy(() => import("@/pages/TemplateEditPage"));
const SharedTransactionPage = lazy(() => import("@/pages/SharedTransactionPage"));
const UnifiedViewPage = lazy(() => import("@/pages/UnifiedViewPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const ToolsPage = lazy(() => import("@/pages/ToolsPage"));
const TutorialHubPage = lazy(() => import("@/pages/TutorialHubPage"));
const BrokerDashboardPage = lazy(() => import("@/pages/BrokerDashboardPage"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const TCDashboardPage = lazy(() => import("@/pages/TCDashboardPage"));
const TCInvitePage = lazy(() => import("@/pages/TCInvitePage"));
const InvitePage = lazy(() => import("@/pages/InvitePage"));
const TCPerformancePage = lazy(() => import("@/pages/TCPerformancePage"));

// Contexts
import { FeaturesProvider } from "@/contexts/FeaturesContext";
import { ShortcutHelpProvider } from "@/components/ShortcutHelpHost";
import { ProductTourProvider } from "@/components/tutorial/ProductTourContext";
import { ProductTourOverlay } from "@/components/tutorial/ProductTourOverlay";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

// API base: set REACT_APP_BACKEND_URL to your deployed API (e.g. https://terrakronos.onrender.com).
// In local dev it can be http://localhost:8000. Never leave this empty in a split-hosting setup
// (Vercel frontend + Render backend) or the browser will call /api on the wrong origin.
const _rawBackend = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
export const BACKEND_URL = _rawBackend;
export const API = `${BACKEND_URL}/api`;

const showDevBypass =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_DEV_BYPASS === "true";

// Auth token management
const AUTH_TOKEN_KEY = 'terrakronos_session_token';
let isLoggingOut = false; // Module-level flag to stop all requests during logout

export const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
};
export const clearAuthToken = () => localStorage.removeItem(AUTH_TOKEN_KEY);

// Logout - set flag to stop all requests, then redirect to backend
export const performLogout = () => {
  isLoggingOut = true; // Stop all axios requests immediately
  localStorage.removeItem(AUTH_TOKEN_KEY);
  // NOTE: We intentionally keep 'tk_session_key' in localStorage.
  // This is the device-specific key used to unlock the MEK for no-password mode.
  // If cleared, the user would lose access to their encrypted documents
  // since the MEK is encrypted with this session key.
  clearSessionKeys(); // Clear MEK from memory (will be restored on next login)
  sessionStorage.setItem('just_logged_out', 'true');
  const returnOrigin = encodeURIComponent(window.location.origin);
  window.location.href = `${BACKEND_URL}/api/auth/logout-redirect?return_origin=${returnOrigin}`;
};

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add request interceptor - BLOCK all requests if logging out
axios.interceptors.request.use(
  (config) => {
    // If we're logging out, cancel all requests except the logout itself
    if (isLoggingOut && !config.url?.includes('/auth/logout')) {
      const error = new Error('Logout in progress');
      error.config = config;
      return Promise.reject(error);
    }

    const method = (config.method || "get").toLowerCase();
    if (!config.timeout && (method === "get" || method === "head")) {
      config.timeout = 25000;
    }
    
    const token = getAuthToken();
    if (token && !config.url?.includes('holiday-tracker')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Ignore errors during logout
    if (isLoggingOut) {
      return Promise.reject(error);
    }

    const retryConfig = error.config;
    if (
      retryConfig?.url &&
      shouldRetryIdempotentRequest(error, {
        method: retryConfig.method,
        retryCount: retryConfig.__retryCount || 0,
      })
    ) {
      retryConfig.__retryCount = (retryConfig.__retryCount || 0) + 1;
      await new Promise((resolve) => setTimeout(resolve, 400 * retryConfig.__retryCount));
      return axios.request(retryConfig);
    }
    
    if (error.response?.status === 401) {
      const path = window.location.pathname || '';
      const isOnLoginPage = path === '/login';
      const isPublicPath =
        path === '/' ||
        path === '/privacy' ||
        path === '/terms' ||
        path === '/faq' ||
        path === '/tools' ||
        path === '/learn' ||
        path.startsWith('/learn/') ||
        path.startsWith('/shared/') ||
        path.startsWith('/unsubscribe/') ||
        path.startsWith('/tc-invite/') ||
        path.startsWith('/invite/');
      const isAuthMeCall = error.config?.url?.includes('/auth/me');
      const isLogoutCall = error.config?.url?.includes('/auth/logout');

      // Never kick viewers off public magic links (or marketing pages)
      // just because a leftover session token returned 401.
      if (isOnLoginPage || isPublicPath || isAuthMeCall || isLogoutCall) {
        return Promise.reject(error);
      }

      // For unexpected 401s on authenticated app pages, force logout
      performLogout();
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await fetchAuthMe();
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      clearAuthToken();
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      clearAuthToken();
    }
  };

  return { user, setUser, loading, checkAuth, logout };
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionToken = params.get("session_token");

      if (sessionToken) {
        try {
          setAuthToken(sessionToken);
          sessionStorage.removeItem("just_logged_out");
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search
          );

          const me = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true,
          });

          const tcInviteRedirect = sessionStorage.getItem("tc_invite_redirect");
          if (tcInviteRedirect) {
            sessionStorage.removeItem("tc_invite_redirect");
            navigate(tcInviteRedirect, {
              state: { user: me.data },
              replace: true,
            });
          } else {
            const loginRedirect = sessionStorage.getItem("login_redirect");
            if (loginRedirect) {
              sessionStorage.removeItem("login_redirect");
              navigate(loginRedirect, {
                state: { user: me.data },
                replace: true,
              });
            } else {
              navigate("/dashboard", {
                state: { user: me.data },
                replace: true,
              });
            }
          }
        } catch (error) {
          console.error("Auth callback error:", error.response?.data || error.message);
          clearAuthToken();
          toast.error(error.response?.data?.detail || "Authentication failed");
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-primary">
        <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    </div>
  );
};

// Protected Route Component - Simple authentication check
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [authToken, setAuthTokenState] = useState(() => getAuthToken());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      showDevBypass &&
      (localStorage.getItem("bypass_mode") === "true" ||
        sessionStorage.getItem("bypass_mode") === "true")
    ) {
      const tok = getAuthToken() || "dev_bypass_local";
      setAuthToken(tok);
      setIsAuthenticated(true);
      setUser({
        user_id: "dev_local_user",
        name: "Development User",
        email: "dev@localhost",
      });
      setAuthTokenState(tok);
      localStorage.setItem("bypass_mode", "true");
      return;
    }

    // Check if user just logged out - redirect immediately without auth check
    if (sessionStorage.getItem("just_logged_out") === "true") {
      sessionStorage.removeItem("just_logged_out");
      navigate("/login?logged_out=true", { replace: true });
      return;
    }
    
    // If user data was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setAuthTokenState(getAuthToken());
      return;
    }

    const checkAuth = async () => {
      const token = getAuthToken();
      const encPromise = token
        ? axios.get(`${API}/encryption/status`).catch(() => null)
        : Promise.resolve(null);
      try {
        const response = await fetchAuthMe();
        setUser(response.data);
        setIsAuthenticated(true);
        setAuthTokenState(getAuthToken());
        
        // Try to unlock encryption if user has it set up
        await unlockUserEncryption(encPromise);
      } catch (error) {
        console.log("Auth check failed:", error.response?.status);
        setIsAuthenticated(false);
        const returnUrl = location.pathname + location.search;
        if (returnUrl && returnUrl !== '/') {
          sessionStorage.setItem('login_redirect', returnUrl);
        }
        navigate("/login", { replace: true });
      }
    };
    
    // Unlock encryption using stored session key
    const unlockUserEncryption = async (encPromise) => {
      try {
        const token = getAuthToken();
        if (!token) return;
        
        const encRes = encPromise ? await encPromise : await axios.get(`${API}/encryption/status`);
        if (!encRes?.data) return;
        const encData = encRes.data;
        
        const sessionKey = localStorage.getItem('tk_session_key');
        if (sessionKey && encData.no_password_mode) {
          const mek = await unlockEncryptionNoPassword(sessionKey, encData);
          let sharingPrivateKey = null;
          if (encData.encrypted_sharing_private_key && encData.sharing_key_nonce) {
            sharingPrivateKey = await unwrapKey(
              encData.encrypted_sharing_private_key,
              mek,
              encData.sharing_key_nonce
            );
          }
          setSessionKeys(mek, sharingPrivateKey);
        }
      } catch (err) {
        console.error('Could not unlock encryption:', err);
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">
          <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <FeaturesProvider key={authToken || 'no-token'} token={authToken}>
      {children({ user, setUser, token: authToken })}
    </FeaturesProvider>
  );
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes("session_token=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <ShortcutHelpProvider>
        <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <DashboardPage user={user} setUser={setUser} />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/transaction/:transactionId"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <TransactionPage user={user} setUser={setUser} />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <TemplatesPage user={user} setUser={setUser} />}
            </ProtectedRoute>
          }
        />
      <Route
        path="/templates/:templateId"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <TemplateEditPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/unified"
        element={
          <ProtectedRoute>
            {({ user, setUser, token }) => <UnifiedViewPage user={user} setUser={setUser} token={token} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user, setUser, token }) => (
              <SettingsPage 
                user={user} 
                token={token} 
                onUserUpdate={() =>
                  axios.get(`${API}/auth/me`).then((res) => setUser(res.data)).catch(() => {})
                }
              />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user, token }) => (
              <AdminPage user={user} token={token} />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/broker"
        element={
          <ProtectedRoute>
            {({ user }) => <BrokerDashboardPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tc-dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <TCDashboardPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tc-performance"
        element={
          <ProtectedRoute>
            {({ user }) => <TCPerformancePage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/shared/:token"
        element={<SharedTransactionPage />}
      />
      {/* Public Tools page - no auth required */}
      <Route
        path="/tools"
        element={<ToolsPage />}
      />
      <Route path="/learn" element={<TutorialHubPage />} />
      <Route path="/learn/:lessonId" element={<TutorialHubPage />} />
      {/* Unsubscribe page - no auth required */}
      <Route
        path="/unsubscribe/:token"
        element={<UnsubscribePage />}
      />
      {/* TC Invite page - no auth required initially (handles login redirect) */}
      <Route
        path="/tc-invite/:token"
        element={<TCInvitePage />}
      />
      {/* Team Invite page - no auth required initially (handles login redirect) */}
      <Route
        path="/invite/:token"
        element={<InvitePage />}
      />
        </Routes>
      </ShortcutHelpProvider>
    </Suspense>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <ProductTourProvider>
          <AppRouter />
          <ProductTourOverlay />
        </ProductTourProvider>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          className: "border-border bg-card text-card-foreground",
        }}
      />
    </div>
  );
}

export default App;
